import { useState, type ReactNode } from "react";
import { pad } from "../../../lib/format.js";
import { useAppLocale } from "../../../lib/useAppLocale.js";
import { useI18n } from "../../../lib/useI18n.js";
import { DatePicker } from "../DatePicker.js";
import { TimePicker } from "../TimePicker.js";
import { toLocalDate } from "../time-helpers.js";
import { ChipPopover } from "./ChipPopover.js";
import { fmtClock, fmtDate, fmtDateTime } from "./palette.js";

type Stage = "date" | "time";

function asDate(dateStr: string): Date | null {
  return dateStr ? new Date(`${dateStr}T00:00:00`) : null;
}

/** Picker content: calendar on the "date" stage, clock on the "time" stage
 *  (timed entries advance to the clock after a day is picked). */
function DateTimeStages({ stage, onStage, dateStr, hour, minute, withTime, onPick, close }: {
  stage: Stage;
  onStage: (s: Stage) => void;
  dateStr: string;
  hour: number | null;
  minute: number | null;
  withTime: boolean;
  onPick: (dateStr: string, hour: number | null, minute: number | null) => void;
  close: () => void;
}) {
  const { t } = useI18n();
  if (stage === "date") {
    return (
      <DatePicker
        value={asDate(dateStr)}
        onSelect={(d) => {
          onPick(toLocalDate(d), hour, minute);
          if (withTime) onStage("time");
          else close();
        }}
      />
    );
  }
  return (
    <div className="pop-stack">
      <TimePicker
        hour={hour}
        minute={minute}
        onChange={(h, m) => onPick(dateStr || toLocalDate(new Date()), h, m)}
      />
      <div className="pop-foot">
        <button type="button" className="btn" onClick={() => onStage("date")}>{t("common.back")}</button>
        <button type="button" className="btn primary" onClick={close}>{t("common.done")}</button>
      </div>
    </div>
  );
}

/** Chip whose date and time parts are separately clickable — the date part
 *  opens the calendar, the time part jumps straight to the clock. */
function SplitDateTimeChip({ ariaLabel, disabled, align, dateText, timeText, emptyText, trailing, dateStr, hour, minute, withTime, onPick }: {
  ariaLabel: string;
  disabled?: boolean;
  align?: "left" | "right";
  dateText: string;
  /** Rendered clickable when set (timed entries with a chosen time). */
  timeText: string | null;
  /** Shown when there's no date at all. */
  emptyText: string;
  trailing?: ReactNode;
  dateStr: string;
  hour: number | null;
  minute: number | null;
  withTime: boolean;
  onPick: (dateStr: string, hour: number | null, minute: number | null) => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>("date");
  const openAt = (s: Stage) => { setStage(s); setOpen(true); };
  const close = () => { setOpen(false); setStage("date"); };
  const part = (s: Stage) => (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    openAt(s);
  };

  return (
    <ChipPopover
      ariaLabel={ariaLabel}
      disabled={disabled ?? false}
      chipClass={disabled ? "is-disabled" : ""}
      align={align ?? "left"}
      open={open}
      onOpenChange={(o) => (o ? openAt("date") : close())}
      chip={
        <>
          {dateText ? (
            <span className="dt-when">
              <span className="dt-part" onClick={part("date")} title={t("taskForm.pickDate")}>{dateText}</span>
              {timeText && (
                <>
                  <span className="dt-sep"> · </span>
                  <span className="dt-part" onClick={part("time")} title={t("taskForm.pickTime")}>{timeText}</span>
                </>
              )}
            </span>
          ) : (
            <span className="dt-when">{emptyText}</span>
          )}
          {trailing}
        </>
      }
    >
      {() => (
        <DateTimeStages
          stage={stage}
          onStage={setStage}
          dateStr={dateStr}
          hour={hour}
          minute={minute}
          withTime={withTime}
          onPick={onPick}
          close={close}
        />
      )}
    </ChipPopover>
  );
}

/** "Starts" chip — calendar + clock popover writing dateStr/hour/minute. */
export function StartsChip({ dateStr, hour, minute, allDay, hour12, onDate }: {
  dateStr: string;
  hour: number | null;
  minute: number | null;
  allDay: boolean;
  hour12: boolean;
  onDate: (dateStr: string, hour: number | null, minute: number | null) => void;
}) {
  const { t } = useI18n();
  const locale = useAppLocale();
  const timed = !allDay && hour !== null && minute !== null;
  return (
    <SplitDateTimeChip
      ariaLabel={t("taskForm.startDateTime")}
      dateText={dateStr ? fmtDateTime(dateStr, null, null, hour12, locale) : ""}
      timeText={timed && dateStr ? fmtClock(hour, minute, hour12, locale) : null}
      emptyText="—"
      withTime={!allDay}
      dateStr={dateStr}
      hour={hour}
      minute={minute}
      onPick={onDate}
      trailing={
        dateStr ? (
          <button
            type="button"
            className="dt-clear"
            onClick={(e) => { e.stopPropagation(); onDate("", null, null); }}
            aria-label={t("taskForm.clearDate")}
            title={t("taskForm.clearDateTitle")}
          >×</button>
        ) : null
      }
    />
  );
}

/** Event "Ends" chip — picks the end instant, written back as duration. */
export function EndsChip({ end, startMs, hour12, allDay, hasStart, onDuration }: {
  end: Date | null;
  startMs: number | null;
  hour12: boolean;
  allDay: boolean;
  hasStart: boolean;
  onDuration: (minutes: number) => void;
}) {
  const { t } = useI18n();
  const locale = useAppLocale();
  const endStr = end ? `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}` : "";
  const pick = (dateStr: string, h: number | null, m: number | null) => {
    if (!startMs) return;
    const picked = new Date(`${dateStr}T${pad(h ?? 0)}:${pad(m ?? 0)}:00`);
    onDuration(Math.max(15, Math.round((picked.getTime() - startMs) / 60_000)));
  };
  return (
    <SplitDateTimeChip
      ariaLabel={t("taskForm.endDateTime")}
      disabled={!end}
      align="right"
      dateText={end ? fmtDateTime(endStr, null, null, hour12, locale) : ""}
      timeText={end ? fmtClock(end.getHours(), end.getMinutes(), hour12, locale) : null}
      emptyText={allDay && hasStart ? t("taskForm.allDay") : "—"}
      withTime
      dateStr={endStr}
      hour={end?.getHours() ?? null}
      minute={end?.getMinutes() ?? null}
      onPick={pick}
    />
  );
}

/** Date-only chip (task deadline, repeat end date). */
export function DateChip({ value, placeholder, onChange, clearable, ariaLabel, chipBase, disabled, align }: {
  value: string;
  placeholder?: string;
  onChange: (dateStr: string) => void;
  clearable?: boolean;
  ariaLabel: string;
  chipBase?: string;
  disabled?: boolean;
  align?: "left" | "right";
}) {
  const { t } = useI18n();
  const locale = useAppLocale();
  return (
    <ChipPopover
      ariaLabel={ariaLabel}
      chipBase={chipBase ?? "dt-chip"}
      disabled={disabled ?? false}
      chipClass={disabled ? "is-disabled" : ""}
      align={align ?? "left"}
      chip={
        <>
          <span className="dt-when">{value ? fmtDate(value, locale) : placeholder ?? "—"}</span>
          {clearable && value && !disabled && (
            <button
              type="button"
              className="dt-clear"
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              aria-label={t("taskForm.clearDate")}
            >×</button>
          )}
        </>
      }
    >
      {(close) => (
        <DatePicker value={asDate(value)} onSelect={(d) => { onChange(toLocalDate(d)); close(); }} />
      )}
    </ChipPopover>
  );
}
